<script setup>
import DialogModal from "../Components/DialogModal.vue";
import { useForm } from "@inertiajs/vue3";
import { onMounted, onUnmounted, reactive, computed } from "vue";
import eventBus from "@/Helpers/eventBus";
import Button from "../Components/Button.vue";
import FormInput from "@/Components/FormInput.vue";
import JetCheckbox from "@/Components/Checkbox.vue";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import JetLabel from "@/Components/InputLabel.vue";
import { getCurrencySymbol, round } from "../Helpers/helperFunctions";
import { usePage } from "@inertiajs/vue3";
import InputError from "@/Components/InputError.vue";
import Checkbox from "@/Components/Checkbox.vue";
import InputLabel from "@/Components/InputLabel.vue";

const props = defineProps({});

const modal = reactive({
    show: false,
});
const form = useForm({
    full_name: null,
    address: null,
    country: null,
    is_company: false,
    company_name: null,
    company_number: null,
    vat_number: null,
    terms: false,
});

function showModal(formData) {
    modal.show = true;

    if (formData) {
        Object.assign(form, formData);
    }
}

function closeModal() {
    //console.log(arguments);
    modal.show = false;
    form.reset();
    form.clearErrors();
}

onMounted(() => {
    // console.log("onMounted CompetitorFormModal");
    eventBus.$on("showTrackEntryFeeFormModal", (argsObj) => {
        showModal(argsObj.formData);
    });
});

onUnmounted(() => {
    // console.log("onUnmounted CompetitorFormModal");
    eventBus.$off("showTrackEntryFeeFormModal");
});

const vatPercentage = computed(() => {
    if (form.country === "LV") {
        return 21;
    }

    if (usePage().props.app.countryCodesInEU.includes(form.country)) {
        // in EU

        if (form.is_company && form.vat_number) {
            return 0;
        }

        return 21;
    }

    // not EU country
    return 0;
});

const vatAmount = computed(() => {
    return round((usePage().props.track.t_entry_fee * vatPercentage.value) / 121, 2);

    // const amount = usePage().props.track.t_entry_fee * (vatPercentage.value / 100);
    // return Math.round((amount + Number.EPSILON) * 100) / 100;
});

const totalToPay = computed(() => {
    const amount = usePage().props.track.t_entry_fee;
    return Math.round((amount + Number.EPSILON) * 100) / 100;
});

function payWithStripeNow() {
    form.post(
        route("track.public.pay-with-stripe-now", {
            pin: usePage().props.track.pin,
            seo_slug: usePage().props.track.seo_slug,
        }),
        {
            preserveScroll: true,
            onSuccess: () => closeModal(),
        },
    );
}
</script>

<template>
    <DialogModal
        :show="modal.show"
        @close="closeModal()"
    >
        <template #title> Access this track</template>

        <template #content>
            <div
                v-if="!$page.props.user"
                class="mt-4 space-y-3"
            >
                Please
                <a
                    target="_top"
                    :href="`/after-auth-intended-track-route/${$page.props.track.pin}/track.public.information`"
                    >Log in</a
                >
                to access this track.
            </div>

            <form
                v-else
                class="space-y-4"
            >
                <div class="col-span-6 sm:col-span-4">Payer information for the entry fee invoice:</div>

                <div class="col-span-6 sm:col-span-4">
                    <FormInput
                        v-model="form.email"
                        label="Email"
                        type="text"
                        disabled
                        :error="form.errors.email"
                    ></FormInput>
                </div>

                <div class="col-span-6 sm:col-span-4">
                    <FormInput
                        v-model="form.full_name"
                        label="Full name"
                        type="text"
                        :error="form.errors.full_name"
                    ></FormInput>
                </div>

                <div class="col-span-6 sm:col-span-4">
                    <FormInput
                        v-model="form.country"
                        type="select"
                        label="Country"
                        :error="form.errors.country"
                        :options="$page.props.app.countries"
                    />
                </div>

                <div class="col-span-6 sm:col-span-4">
                    <FormInput
                        v-model="form.address"
                        label="Full address"
                        type="text"
                        :error="form.errors.address"
                    ></FormInput>
                </div>

                <div class="col-span-6 sm:col-span-4">
                    <JetLabel>
                        <div class="flex items-center">
                            <JetCheckbox v-model:checked="form.is_company" />
                            <div class="ml-2">Entry fee paid by company</div>
                        </div>
                    </JetLabel>
                </div>

                <div
                    v-if="form.is_company"
                    class="space-y-4"
                >
                    <div class="col-span-6 sm:col-span-4">
                        <FormInput
                            v-model="form.company_name"
                            label="Company name"
                            type="text"
                            :error="form.errors.company_name"
                        ></FormInput>
                    </div>

                    <div class="col-span-6 sm:col-span-4">
                        <FormInput
                            v-model="form.company_number"
                            label="Company registration number"
                            type="text"
                            :error="form.errors.company_number"
                        ></FormInput>
                    </div>

                    <div class="col-span-6 sm:col-span-4">
                        <FormInput
                            v-model="form.vat_number"
                            label="Company VAT number"
                            type="text"
                            :error="form.errors.vat_number"
                        ></FormInput>
                    </div>
                </div>

                <hr />

                <InputLabel for="terms">
                    <div class="flex items-center">
                        <Checkbox
                            id="terms"
                            v-model:checked="form.terms"
                            name="terms"
                            required
                        />

                        <div class="ml-2">
                            I agree to the
                            <a
                                target="_blank"
                                :href="route('terms.show')"
                                class="text-sm"
                                >Terms of Service</a
                            >
                            and
                            <a
                                target="_blank"
                                :href="route('policy.show')"
                                class="text-sm"
                                >Privacy Policy</a
                            >
                        </div>
                    </div>
                    <InputError
                        class="mt-2"
                        :message="form.errors.terms"
                    />
                </InputLabel>

                <div class="space-y-4 border-t pt-4 text-right tabular-nums">
                    <div class="space-y-4">
                        Track entry fee (for {{ $page.props.track.access_duration_days }} days):
                        {{ getCurrencySymbol($page.props.track.t_entry_fee_currency)
                        }}{{ $page.props.track.t_entry_fee.toFixed(2) }}
                    </div>
                    <!--                    <div class="space-y-4">
                        VAT ({{ vatPercentage }}%):
                        {{ getCurrencySymbol($page.props.track.t_entry_fee_currency)
                        }}{{ vatAmount.toFixed(2) }}
                    </div>-->
                    <div class="space-y-4 font-semibold">
                        Total: {{ getCurrencySymbol($page.props.track.t_entry_fee_currency)
                        }}{{ totalToPay.toFixed(2) }}
                    </div>
                </div>
            </form>
        </template>

        <template #footer>
            <Button
                color="white"
                @click="closeModal()"
            >
                Cancel
            </Button>

            <Button
                v-if="$page.props.user"
                class="ml-3"
                :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing"
                @click="payWithStripeNow()"
            >
                Pay with Stripe now
            </Button>
        </template>
    </DialogModal>
</template>
